/**
 * Login Prompt Modal Component
 *
 * Shows multiple provider login options (Spotify, Apple Music, etc.)
 * Matches original multi-provider login UI structure.
 */

import { signal } from '@preact/signals';
import { getProviderAuthUrl, PROVIDERS, connectAppleMusic } from '@/stores/auth';
import { showLoginPrompt, hideLoginPrompt } from '@/stores/ui';

const isConnecting = signal(false);
const connectError = signal<string | null>(null);

export function LoginPromptModal() {
  // Access .value in render to subscribe to changes
  const isVisible = showLoginPrompt.value;
  if (!isVisible) return null;

  const handleProviderLogin = async (providerId: string) => {
    // Apple Music uses MusicKit JS directly (needs user click for popup)
    if (providerId === 'apple_music') {
      isConnecting.value = true;
      connectError.value = null;
      try {
        const success = await connectAppleMusic();
        if (success) {
          hideLoginPrompt();
        } else {
          connectError.value = 'Failed to connect Apple Music. Please allow popups.';
        }
      } catch (e) {
        console.error('Apple Music connect error:', e);
        connectError.value = e instanceof Error ? e.message : 'Connection failed';
      } finally {
        isConnecting.value = false;
      }
      return;
    }

    // Other providers use OAuth redirect
    window.location.href = getProviderAuthUrl(providerId as any);
  };

  const handleClose = () => {
    hideLoginPrompt();
  };

  const handleOverlayClick = (e: Event) => {
    if ((e.target as HTMLElement).classList.contains('login-overlay')) {
      handleClose();
    }
  };

  // Get enabled providers
  const enabledProviders = PROVIDERS.filter(p => p.enabled);

  return (
    <div id="login-prompt-modal" style={{ display: 'block' }} onClick={handleOverlayClick}>
      <div class="login-overlay"></div>
      <div class="login-content">
        <h3>Join the Music Community</h3>
        <p>Log in to vote on songs and share your own favorites!</p>

        <div class="provider-buttons">
          {enabledProviders.map(provider => (
            <button
              key={provider.id}
              class="provider-login-btn"
              data-provider={provider.id}
              style={{ '--provider-color': provider.color } as any}
              onClick={() => handleProviderLogin(provider.id)}
              disabled={isConnecting.value}
            >
              <img
                src={`/images/providers/${provider.id}.svg`}
                alt={provider.name}
                class="provider-icon"
              />
              <span>
                {isConnecting.value && provider.id === 'apple_music'
                  ? 'Connecting...'
                  : `Continue with ${provider.name}`}
              </span>
            </button>
          ))}
        </div>

        {connectError.value && (
          <p class="error-message" style={{ color: '#ff3e4d', marginTop: '12px', fontSize: '14px' }}>
            {connectError.value}
          </p>
        )}

        <button class="close-prompt-btn" onClick={handleClose}>Maybe Later</button>
      </div>
    </div>
  );
}
